import { create } from 'zustand';
import { ColumnState } from 'ag-grid-community';
import { PUR5010_R01ItemSubtotalData, PUR5010_R01WarehouseSubtotalData, PUR5010_R01BrandSubtotalData, PUR5010_R01Dto } from './Dto_PUR5010_R01';

// 탭 타입 정의 및 UI 상태 타입 (재고)
export type PUR5010_R01TabType = 'item' | 'warehouse' | 'brand';

export interface PUR5010_R01TabConfig {
    id: PUR5010_R01TabType;
    label: string;
    description: string;
}

export type PUR5010_R01DateStockData = any;
export type PUR5010_R01ItemStockData = any;
export type PUR5010_R01WorkplaceStockData = any;   
export type PUR5010_R01BrandStockData = any;

export interface PUR5010_R01TabData {
    item: PUR5010_R01ItemStockData[];
    warehouse: PUR5010_R01WorkplaceStockData[];
    brand: PUR5010_R01BrandStockData[];
}

// 소계 데이터 인터페이스
export interface PUR5010_R01TabSubtotalData {
    item: PUR5010_R01ItemSubtotalData[];
    warehouse: PUR5010_R01WarehouseSubtotalData[];
    brand: PUR5010_R01BrandSubtotalData[];
}

// 그리드 데이터 인터페이스 (소계 포함)
export interface PUR5010_R01TabGridData {
    item: PUR5010_R01Dto[];
    warehouse: PUR5010_R01Dto[];
    brand: PUR5010_R01Dto[];
}

export interface PUR5010_R01TabColumnStates {
    item: ColumnState[];
    warehouse: ColumnState[];
    brand: ColumnState[];
}

export interface PUR5010_R01TabLoadingStates {
    item: boolean;
    warehouse: boolean;
    brand: boolean;
}

// 탭별 선택된 행 ID 목록
export interface PUR5010_R01TabSelectedIds {
    item: string[];
    warehouse: string[];
    brand: string[];
}

export interface PUR5010_R01TabScrollTop {
    item: number | null;
    warehouse: number | null;
    brand: number | null;
}

const defaultPUR5010_R01TabScrollTop = (): PUR5010_R01TabScrollTop => ({
    item: null,
    warehouse: null,
    brand: null,
});

// 필터 인터페이스 (API에서 사용)
export interface PUR5010_R01Filter {
    logicCenter: string;
    warehouseCd: string;
    itemCd: string;
    itemNm: string;
    brandCd: string;
    itemStatus: string;
    compCd: string;
    itemAccount: string;
    itemGroup: string;
}

interface PUR5010_R01Store {
    // 기본 필터
    PUR5010_R01Filter: PUR5010_R01Filter;
    setPUR5010_R01Filter: (filter: Partial<PUR5010_R01Filter>) => void;
    
    
    // 활성 탭
    PUR5010_R01activeTab: PUR5010_R01TabType;
    setPUR5010_R01ActiveTab: (tab: PUR5010_R01TabType) => void;
    
    // 탭별 데이터
    PUR5010_R01tabData: PUR5010_R01TabData;
    setPUR5010_R01TabData: (tabType: PUR5010_R01TabType, data: any[]) => void;
    
    // 탭별 컬럼 상태
    PUR5010_R01tabColumnStates: PUR5010_R01TabColumnStates;
    setPUR5010_R01TabColumnState: (tabType: PUR5010_R01TabType, columnState: any[]) => void;
    
    // 탭별 로딩 상태
    PUR5010_R01tabLoadingStates: PUR5010_R01TabLoadingStates;
    setPUR5010_R01TabLoadingState: (tabType: PUR5010_R01TabType, loading: boolean) => void;
    
    // 탭별 선택 상태
    PUR5010_R01tabSelectedIds: PUR5010_R01TabSelectedIds;
    setPUR5010_R01TabSelectedIds: (tabType: PUR5010_R01TabType, ids: string[]) => void;
    PUR5010_R01toggleSelectedId: (tabType: PUR5010_R01TabType, id: string) => void;
    PUR5010_R01clearTabSelectedIds: (tabType: PUR5010_R01TabType) => void;

    PUR5010_R01tabScrollTop: PUR5010_R01TabScrollTop;
    setPUR5010_R01TabScrollTop: (tabType: PUR5010_R01TabType, top: number | null) => void;
    resetAllPUR5010_R01TabScrollTop: () => void;
    
    // === 소계 관련 상태 ===
    PUR5010_R01showSubtotals: boolean;
    setPUR5010_R01ShowSubtotals: (show: boolean) => void;
    
    // 탭별 소계 데이터
    PUR5010_R01tabSubtotalData: PUR5010_R01TabSubtotalData;
    setPUR5010_R01TabSubtotalData: (tabType: PUR5010_R01TabType, data: any[]) => void;
    
    // 탭별 그리드 데이터 (소계 포함)
    PUR5010_R01tabGridData: PUR5010_R01TabGridData;
    setPUR5010_R01TabGridData: (tabType: PUR5010_R01TabType, data: PUR5010_R01Dto[]) => void;
    
    // 초기화
    initData: () => void;
}

export const defaultPUR5010_R01Filter: PUR5010_R01Filter = {
    logicCenter: '',
    warehouseCd: '',
    itemCd: '',
    itemNm: '',
    brandCd: '',
    itemStatus: '',
    compCd: '',
    itemAccount: '',
    itemGroup: '',
}

export const usePUR5010_R01Store = create<PUR5010_R01Store>((set, get) => ({
    // 기본 필터
    PUR5010_R01Filter: {
        logicCenter: '',
        warehouseCd: '',
        itemCd: '',
        itemNm: '',
        brandCd: '',
        itemStatus: '',
        compCd: '',
        itemAccount: '',
        itemGroup: '',
    },
    setPUR5010_R01Filter: (filter) => set((state) => ({
        PUR5010_R01Filter: { ...state.PUR5010_R01Filter, ...filter }
    })),
    
    // 활성 탭
    PUR5010_R01activeTab: 'item',
    setPUR5010_R01ActiveTab: (tab) => {
        set({ PUR5010_R01activeTab: tab });
    },
    
    // 탭별 데이터
    PUR5010_R01tabData: {
        item: [],
        warehouse: [],
        brand: [],
    },
    setPUR5010_R01TabData: (tabType, data) => set((state) => ({
        PUR5010_R01tabData: {
            ...state.PUR5010_R01tabData,
            [tabType]: data
        }
    })),
    
    // 탭별 컬럼 상태
    PUR5010_R01tabColumnStates: {
        item: [],
        warehouse: [],
        brand: [],
    },
    setPUR5010_R01TabColumnState: (tabType, columnState) => set((state) => ({
        PUR5010_R01tabColumnStates: {
            ...state.PUR5010_R01tabColumnStates,
            [tabType]: columnState
        }
    })),
    
    // 탭별 로딩 상태
    PUR5010_R01tabLoadingStates: {
        item: false,
        warehouse: false,
        brand: false,
    },
    setPUR5010_R01TabLoadingState: (tabType, loading) => set((state) => ({
        PUR5010_R01tabLoadingStates: {
            ...state.PUR5010_R01tabLoadingStates,
            [tabType]: loading
        }
    })),
    
    // 탭별 선택 상태
    PUR5010_R01tabSelectedIds: {
        item: [],
        warehouse: [],
        brand: [],
    },
    setPUR5010_R01TabSelectedIds: (tabType, ids) => set((state) => ({
        PUR5010_R01tabSelectedIds: {
            ...state.PUR5010_R01tabSelectedIds,
            [tabType]: ids
        }
    })),
    PUR5010_R01toggleSelectedId: (tabType, id) => set((state) => {
        const current = state.PUR5010_R01tabSelectedIds[tabType] || [];
        const exists = current.includes(id);
        const next = exists ? current.filter(v => v !== id) : [...current, id];
        return {
            tabSelectedIds: {
                ...state.PUR5010_R01tabSelectedIds,
                [tabType]: next
            }
        } as any;
    }),
    PUR5010_R01clearTabSelectedIds: (tabType) => set((state) => ({
        PUR5010_R01tabSelectedIds: {
            ...state.PUR5010_R01tabSelectedIds,
            [tabType]: []
        }
    })),

    PUR5010_R01tabScrollTop: defaultPUR5010_R01TabScrollTop(),
    setPUR5010_R01TabScrollTop: (tabType, top) => set((state) => ({
        PUR5010_R01tabScrollTop: {
            ...state.PUR5010_R01tabScrollTop,
            [tabType]: top,
        },
    })),
    resetAllPUR5010_R01TabScrollTop: () => set({ PUR5010_R01tabScrollTop: defaultPUR5010_R01TabScrollTop() }),
    
    // === 소계 관련 상태 ===
    PUR5010_R01showSubtotals: false,
    setPUR5010_R01ShowSubtotals: (show) => set({ PUR5010_R01showSubtotals: show }),
    
    // 탭별 소계 데이터
    PUR5010_R01tabSubtotalData: {
        item: [],
        warehouse: [],
        brand: [],
    },
    setPUR5010_R01TabSubtotalData: (tabType, data) => set((state) => ({
        PUR5010_R01tabSubtotalData: {
            ...state.PUR5010_R01tabSubtotalData,
            [tabType]: data
        }
    })),
    
    // 탭별 그리드 데이터 (소계 포함)
    PUR5010_R01tabGridData: {
        item: [],
        warehouse: [],
        brand: [],
    },
    setPUR5010_R01TabGridData: (tabType, data) => set((state) => ({
        PUR5010_R01tabGridData: {
            ...state.PUR5010_R01tabGridData,
            [tabType]: data
        }
    })),
    
    // 초기화
    initData: () => set({
        PUR5010_R01Filter: {
            logicCenter: '',
            warehouseCd: '',
            itemCd: '',
            itemNm: '',
            brandCd: '',
            itemStatus: '',
            compCd: '',
            itemAccount: '',
            itemGroup: '',
        },
        PUR5010_R01activeTab: 'item',
        PUR5010_R01tabData: {
            item: [],
            warehouse: [],
            brand: [],
        },
        PUR5010_R01tabColumnStates: {
            item: [],
            warehouse: [],
            brand: [],
        },
        PUR5010_R01tabLoadingStates: {
            item: false,
            warehouse: false,
            brand: false,
        },
        PUR5010_R01tabSelectedIds: {
            item: [],
            warehouse: [],
            brand: [],
        },
        PUR5010_R01showSubtotals: false,
        PUR5010_R01tabSubtotalData: {
            item: [],
            warehouse: [],
            brand: [],
        },
        PUR5010_R01tabGridData: {
            item: [],
            warehouse: [],
            brand: [],
        },
        PUR5010_R01tabScrollTop: defaultPUR5010_R01TabScrollTop(),
    }),
}));

export default usePUR5010_R01Store;
